import { AxiosError } from 'axios';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import {
  createHiringRequestReference,
  createReference,
  deleteHiringRequestReference,
  editHiringRequestReference,
  requestReference,
} from './referenceQueries';
import {
  CreateReferenceContactMutationVariables,
  CreateReferenceMutationVariables,
  CreateRequestMutationVariables,
  DeleteReferenceMutationVariables,
  EditReferenceMutationVariables,
  ReferenceRequestPayload,
  UpdateHiringRequestVariables,
} from '../types/referenceTypes';
import { HiringRequestListAPIData, UpdateHiringRequestResponseData } from '../types';
import { hiringRequestQueries } from 'queryKeys/hiringRequest';
import { HiringRequestScorecard } from '../types/feedbackDetailTypes';
import { Reference as ReferenceResponse, ReferenceCheck } from 'types';
import hiringRequestsAPI from 'api/hiringRequestsAPI';

export function useCreateReferenceRequest(
  candidateId: number,
  options?: Omit<
    UseMutationOptions<
      CreateRequestMutationVariables,
      AxiosError,
      ReferenceRequestPayload,
      unknown
    >,
    'mutationFn'
  >
): UseMutationResult<CreateRequestMutationVariables, AxiosError, ReferenceRequestPayload, unknown> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => requestReference(candidateId, payload),
    ...options,
    onSuccess: (data, variables, context) => {
      const queryKey = hiringRequestQueries.hiringRequests.pendingReferences(candidateId).queryKey;

      queryClient.invalidateQueries({
        queryKey,
        refetchType: 'all',
        exact: true,
      });

      options.onSuccess?.(data, variables, context);
    },
  });
}

export function useCreateHiringRequestReference(
  candidateId: number,
  options?: Omit<
    UseMutationOptions<ReferenceCheck, AxiosError, CreateReferenceMutationVariables, unknown>,
    'mutationFn'
  >
): UseMutationResult<ReferenceCheck, AxiosError, CreateReferenceMutationVariables, unknown> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload }) => createHiringRequestReference(payload),
    ...options,
    onSuccess: (data, variables, context) => {
      const queryKey =
        hiringRequestQueries.hiringRequests.completedAndDraftReferences(candidateId).queryKey;

      queryClient.invalidateQueries({
        queryKey,
        refetchType: 'all',
        exact: true,
      });

      options.onSuccess?.(data, variables, context);
    },
  });
}

export function useCreateReferenceContact(
  candidateId: number,
  options?: Omit<
    UseMutationOptions<
      ReferenceResponse,
      AxiosError,
      CreateReferenceContactMutationVariables,
      unknown
    >,
    'mutationFn'
  >
): UseMutationResult<
  ReferenceResponse,
  AxiosError,
  CreateReferenceContactMutationVariables,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload }) => createReference(payload),
    onSuccess: (data, variables, context) => {
      const currentQueryData: ReferenceResponse[] = queryClient.getQueryData(
        hiringRequestQueries.hiringRequests.referenceContacts(candidateId).queryKey
      );

      const updatedData = currentQueryData.concat(data);

      queryClient.setQueryData(
        hiringRequestQueries.hiringRequests.referenceContacts(candidateId).queryKey,
        updatedData
      );

      options.onSuccess?.(data, variables, context);
    },
  });
}

export function useEditHiringRequestReference(
  candidateId: number,
  referenceId: number,
  options?: Omit<
    UseMutationOptions<ReferenceResponse, AxiosError, EditReferenceMutationVariables, unknown>,
    'mutationFn'
  >
): UseMutationResult<ReferenceResponse, AxiosError, EditReferenceMutationVariables, unknown> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload }) => {
      return editHiringRequestReference(referenceId, payload);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      const queryKey =
        hiringRequestQueries.hiringRequests.completedAndDraftReferences(candidateId).queryKey;

      queryClient.invalidateQueries({
        queryKey,
        refetchType: 'all',
        exact: true,
      });

      options.onSuccess?.(data, variables, context);
    },
  });
}

export function useDeleteHiringRequestReference(
  options?: Omit<
    UseMutationOptions<HiringRequestScorecard, unknown, DeleteReferenceMutationVariables, unknown>,
    'mutationFn'
  >
): UseMutationResult<HiringRequestScorecard, unknown, DeleteReferenceMutationVariables, unknown> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ referenceId }) => {
      const response = await deleteHiringRequestReference(referenceId);
      return response;
    },
    ...options,
    onSuccess: (data, variables, context) => {
      options.onSuccess?.(data, variables, context);

      const currentQueryData: HiringRequestScorecard[] = queryClient.getQueryData(
        hiringRequestQueries.hiringRequests.completedAndDraftReferences(variables.candidateId)
          .queryKey
      );

      const updatedData = currentQueryData.filter((datum) => datum.id !== variables.referenceId);

      queryClient.setQueryData(
        hiringRequestQueries.hiringRequests.completedAndDraftReferences(variables.candidateId)
          .queryKey,
        updatedData
      );
    },
  });
}

export function useMutationUpdateHiringRequest(
  districtId: number,
  options: Omit<
    UseMutationOptions<
      UpdateHiringRequestResponseData,
      AxiosError,
      UpdateHiringRequestVariables,
      undefined
    >,
    'mutationFn'
  > = {}
): UseMutationResult<
  UpdateHiringRequestResponseData,
  AxiosError,
  UpdateHiringRequestVariables,
  undefined
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ hiringRequestId, payload }) =>
      hiringRequestsAPI.updateHiringRequest(hiringRequestId, payload),
    ...options,
    onSuccess: (data, variables, context) => {
      options.onSuccess?.(data, variables, context);

      const currentListData: HiringRequestListAPIData = queryClient.getQueryData(
        hiringRequestQueries.hiringRequests.list(districtId).queryKey
      );
      const updatedResults = currentListData.results.map((item) =>
        item.id === variables.hiringRequestId ? { ...item, status: data.status } : item
      );

      const updatedData = {
        ...currentListData,
        results: updatedResults,
      };

      queryClient.setQueryData(
        hiringRequestQueries.hiringRequests.list(districtId).queryKey,
        updatedData
      );
    },
  });
}
